import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Typography, Card, Avatar, Rate, Input, Button, Spin, Empty,
    Flex, Grid, message
} from "antd";
import { CarOutlined, ShoppingOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { User } from "../models/User";
import { OrderItem } from "../models/OrderItem";
import { OrderService } from "../services/OrderService";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer from "./snipets/Footer";
import { goToRestaurantsWithLocation } from "../Utils/goToRestaurantsWithLocation";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BRAND       = "#00AF66";
const BRAND_LIGHT = "#E6F7EF";

const RATING_LABELS: Record<number, string> = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
};

type FoodRating = {
    orderId: number;
    rating: number;
    comment: string; // optional — can be empty string
};

const RateOrderPage: React.FC = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [driver,       setDriver]       = useState<User>(new User());
    const [orderItem,    setOrderItem]    = useState<OrderItem[]>([]);
    const [loadingData,  setLoadingData]  = useState(true);
    const [driverRating, setDriverRating] = useState(0);
    const [foodRatings,  setFoodRatings]  = useState<FoodRating[]>([]);
    const [submitting,   setSubmitting]   = useState(false);

    const commentRefs = useRef<Record<number, string>>({});

    // ── Fetch last driver + order food items on mount ─────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const orderItems: OrderItem[] = await OrderService.getLastOrdersByUser();
                setOrderItem(orderItems);
                setDriver(orderItems[0].driver);
                setFoodRatings(
                    orderItems.map((item) => ({
                        orderId: item.id,
                        rating: 0,
                        comment: "",
                    }))
                );
            } catch (e) {
                message.error("Could not load order details. Please try again.");
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, []);

    // ── Food rating helpers ───────────────────────────────────────────────────
    const setFoodRating = (foodId: number, rating: number) => {
        setFoodRatings((prev) =>
            prev.map((r) => (r.orderId === foodId ? { ...r, rating } : r))
        );
    };

    const flushComment = (foodId: number) => {
        const comment = commentRefs.current[foodId] ?? "";
        setFoodRatings((prev) =>
            prev.map((r) => (r.orderId === foodId ? { ...r, comment } : r))
        );
    };

    const getFoodRating = (orderId: number): FoodRating =>
        foodRatings.find((r) => r.orderId === orderId) ?? { orderId, rating: 0, comment: "" };

    // ── Validation — comments NOT required, only stars ────────────────────────
    const allRated =
        driverRating > 0 &&
        foodRatings.length > 0 &&
        foodRatings.every((r) => r.rating > 0);

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!driver || !allRated) return;
        setSubmitting(true);
        try {
            await OrderService.rateOrder({
                order_rating: foodRatings,
                driver: { id: driver.id, rating: driverRating },
            });
            goToRestaurantsWithLocation(navigate);
        } catch (e) {
            message.error("Could not submit your rating. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loadingData) {
        return (
            <div style={{ width: "100%", overflowX: "hidden" }}>
                <TopNavigation />
                <Flex
                    vertical align="center" justify="center" gap={12}
                    style={{ paddingTop: HEADER_HEIGHT, minHeight: "100dvh", background: "#F7F9FC" }}
                >
                    <Spin size="large" />
                    <Text type="secondary">Loading order details...</Text>
                </Flex>
            </div>
        );
    }

    // ── No data ───────────────────────────────────────────────────────────────
    if (!driver || orderItem.length === 0) {
        return (
            <div style={{ width: "100%", overflowX: "hidden" }}>
                <TopNavigation />
                <Flex
                    vertical align="center" justify="center"
                    style={{ paddingTop: HEADER_HEIGHT, minHeight: "100dvh", background: "#F7F9FC", padding: "0 32px" }}
                >
                    <Empty description="No recent order found." />
                    <Button type="primary" style={{ marginTop: 16, background: BRAND, borderColor: BRAND }} onClick={() => navigate("/")}>
                        Go back home
                    </Button>
                </Flex>
            </div>
        );
    }

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <div style={{ width: "100%", overflowX: "hidden" }}>
            <TopNavigation />

            <div style={{ paddingTop: HEADER_HEIGHT, background: "#F7F9FC", minHeight: "100dvh" }}>
                <Flex vertical style={{ maxWidth: 640, margin: "0 auto", padding: screens.xs ? 16 : 24 }} gap={12}>

                    <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
                        <Title level={3} style={{ margin: 0 }}>Rate your order</Title>
                    </Flex>

                    {/* ══════════ SECTION 1 — Rate your driver ══════════ */}
                    <Flex align="center" gap={8} style={{ marginTop: 8 }}>
                        <CarOutlined style={{ color: BRAND, fontSize: 20 }} />
                        <Title level={5} style={{ margin: 0 }}>Rate your driver</Title>
                    </Flex>

                    <Card>
                        <Flex align="center" gap={12}>
                            <Avatar
                                size={52}
                                style={{ background: BRAND_LIGHT, color: BRAND, border: `2px solid ${BRAND}`, fontWeight: 800 }}
                            >
                                {driver.name.charAt(0).toUpperCase()}{driver.surname.charAt(0).toUpperCase()}
                            </Avatar>
                            <Flex vertical style={{ flex: 1 }}>
                                <Text strong>{driver.name} {driver.surname}</Text>
                                <Text type="secondary" style={{ fontSize: 13 }}>{driver.phone}</Text>
                            </Flex>
                        </Flex>

                        <div style={{ borderTop: "1px solid #EDF1F7", margin: "12px 0" }} />

                        <Flex vertical gap={8}>
                            <Rate value={driverRating} onChange={setDriverRating} style={{ fontSize: 25 }} />
                            {driverRating > 0 && (
                                <Text strong style={{ color: BRAND, fontSize: 13 }}>{RATING_LABELS[driverRating]}</Text>
                            )}
                        </Flex>
                    </Card>

                    {/* ══════════ SECTION 2 — Rate your food ══════════ */}
                    <Flex align="center" gap={8} style={{ marginTop: 16 }}>
                        <ShoppingOutlined style={{ color: BRAND, fontSize: 20 }} />
                        <Title level={5} style={{ margin: 0 }}>Rate your food</Title>
                    </Flex>

                    {orderItem.map((item) => {
                        const fr = getFoodRating(item.id);
                        return (
                            <Card key={item.id}>
                                <Flex align="center" gap={12}>
                                    <Flex
                                        align="center" justify="center"
                                        style={{ width: 48, height: 48, borderRadius: 12, background: BRAND_LIGHT, flexShrink: 0 }}
                                    >
                                        <span style={{ fontSize: 24 }}>🍽️</span>
                                    </Flex>
                                    <Flex vertical style={{ flex: 1 }}>
                                        <Text strong>{item.menu_item.name}</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Qty: {item.quantity}</Text>
                                    </Flex>
                                    {fr.rating > 0 && (
                                        <Text strong style={{ color: BRAND, fontSize: 13 }}>{RATING_LABELS[fr.rating]}</Text>
                                    )}
                                </Flex>

                                <div style={{ borderTop: "1px solid #EDF1F7", margin: "12px 0" }} />

                                <Rate
                                    value={fr.rating}
                                    onChange={(val) => setFoodRating(item.id, val)}
                                    style={{ fontSize: 25, marginBottom: 12 }}
                                />

                                <Input.TextArea
                                    placeholder="Add a comment... (optional)"
                                    defaultValue={fr.comment}
                                    onChange={(e) => { commentRefs.current[item.id] = e.target.value; }}
                                    onBlur={() => flushComment(item.id)}
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                />
                            </Card>
                        );
                    })}

                    {!allRated && (
                        <Text type="secondary" style={{ fontSize: 13, textAlign: "center", margin: "4px 0" }}>
                            Please rate the driver and all food items to continue.
                        </Text>
                    )}

                    <Button
                        type="primary"
                        size="large"
                        block
                        disabled={!allRated}
                        loading={submitting}
                        onClick={handleSubmit}
                        style={{ background: BRAND, borderColor: BRAND, marginTop: 8, fontWeight: 700 }}
                    >
                        Submit ratings
                    </Button>

                </Flex>
            </div>

            <Footer />
        </div>
    );
};

export default RateOrderPage;